"use client"

/* ds-typo-ignore-file — EN-only prototype ad LP: intentional sub-DS sizes in the immersive scroll readout (status badges) (not shipped DS surfaces) */

import Image from "next/image"
import { bloodPriceArg } from "@/lib/blood-price-arg"
import { useCountry } from "@/components/layout/country-provider"
import { useLivePrices } from "@/lib/use-live-prices"
import { useState, useEffect, useRef, useCallback } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { goKeys as k } from "@/i18n/go-keys"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { blobMediaUrl } from "@/lib/blob-url"
import { INSIGHTS_V2 } from "./metabolism-data"
import {
  MetabolismReadingChip,
  type ReadingChart,
  type Finding,
} from "./metabolism-reading-chip"
import {
  SECTION,
  BookingLink,
  CtaLabel,
  useBookingHref,
} from "./metabolism-components"

/* ── Immersive full-picture variant — scroll-pinned blur reveal.
   Sharp photo + heading first; on scroll the photo pins, blurs and darkens
   while the readout reveals over it. Full-bleed. Scroll progress drives CSS
   vars on a rAF loop (no per-frame re-render). The tuning values below were
   finalised on the immunity LP and inlined here. ── */
type ImmersiveCfg = {
  trackVh: number
  blurStart: number
  blurEnd: number
  maxBlur: number
  darken: number
  revealStart: number
  revealEnd: number
  riseLeft: number
  risePanel: number
  fadeBand: number
}

const IMMERSIVE_DEFAULTS: ImmersiveCfg = {
  trackVh: 240,
  blurStart: 0.04,
  blurEnd: 0.2,
  maxBlur: 16,
  darken: 0.14,
  revealStart: 0.2,
  revealEnd: 0.9,
  // Slide ratio: 1 = the block travels exactly the scroll distance of the
  // reveal window (moves 1:1 with the page scroll). < 1 = slower parallax.
  riseLeft: 1,
  risePanel: 1,
  // The block stays invisible below the fold and fades 0→1 over this many px
  // as it rises past the viewport bottom — so it materialises low in view,
  // not solid from the very bottom edge.
  fadeBand: 150,
}

function StatusBadge({
  tone,
  label,
}: {
  tone: "green" | "sand" | "terra"
  label: string
}) {
  const cls = {
    green: "bg-[#e0e6b0] text-[#596625]",
    sand: "bg-[#f2efe8] text-[#7a5c4c]",
    terra: "bg-[#f7e1d8] text-[#b0432d]",
  }[tone]
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-medium ${cls}`}
    >
      {label}
    </span>
  )
}

const ZONE_COLOR: Record<string, string> = {
  sand: "#b79c80",
  green: "#95a73b",
  terra: "#e96f3f",
}

export function ZoneBar({
  zones,
  dot,
}: {
  zones: readonly { c: string; w: number }[]
  dot: number
}) {
  return (
    <div className="relative h-[6px] w-full">
      <div className="flex h-full w-full gap-[3px]">
        {zones.map((z, i) => (
          <div
            key={i}
            className="h-full rounded-full"
            style={{ width: `${z.w}%`, background: ZONE_COLOR[z.c] }}
          />
        ))}
      </div>
      <div
        className="absolute top-1/2 size-[15px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-black bg-white"
        style={{ left: `${dot}%` }}
      />
    </div>
  )
}

export function ImmersiveFullPicture({
  activeIndex,
  onPick,
}: {
  activeIndex: number
  onPick: (i: number) => void
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const totalRef = useRef(0) // pinned-scroll length in px (for 1:1 slide)
  const active = INSIGHTS_V2[activeIndex]
  const t = useTranslations("go")
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const bookingHref = useBookingHref()

  // Finalised behaviour, inlined from the immunity tuner (no runtime controls).
  const cfg = IMMERSIVE_DEFAULTS
  const [autoplay, setAutoplay] = useState(true)
  const autoDur = 7
  const [started, setStarted] = useState(false)
  // Pause auto-advance while the pointer is over the block — gives people time
  // to read without the story moving on under them (best practice for
  // auto-rotating content).
  const [hovered, setHovered] = useState(false)
  // Never auto-advance under reduced motion: the progress bar is CSS-driven, so
  // with durations zeroed its animationend would fire instantly and race.
  const [reduced, setReduced] = useState(false)
  const cfgRef = useRef(cfg)
  cfgRef.current = cfg

  const applyProgress = useCallback((p: number) => {
    const stage = stageRef.current
    if (!stage) return
    const c = cfgRef.current
    const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v))
    // Photo blur: quick cross-fade over a short early window.
    const t = clamp((p - c.blurStart) / Math.max(0.01, c.blurEnd - c.blurStart))
    // Reveal slide: LINEAR over the reveal window so it tracks the scroll 1:1.
    const rl = clamp(
      (p - c.revealStart) / Math.max(0.01, c.revealEnd - c.revealStart)
    )
    // 1:1 slide: the block travels exactly the scroll distance of the reveal
    // window (× ratio), so it enters at normal page-scroll speed.
    const intervalPx = (c.revealEnd - c.revealStart) * totalRef.current
    // Opacity follows the block's on-screen position, not the raw progress:
    // it stays invisible while below the fold and fades 0→1 over `fadeBand` px
    // once it rises past the viewport bottom — so it materialises low in view
    // (≈fadeBand px up), not solid from the very bottom edge.
    const offsetPx = (1 - rl) * c.risePanel * intervalPx
    const oe = clamp(
      (window.innerHeight / 2 - offsetPx) / Math.max(1, c.fadeBand)
    )
    const op = oe * oe * (3 - 2 * oe)
    // Header fade: the intro heading/subhead must fully clear before the
    // readout (tabs + card) fades in, otherwise the two collide mid-scroll
    // (worst on mobile, where both layers are centred in the same column).
    // Fade it out over the first third of the reveal window — by the time the
    // readout materialises it's gone, not lingering underneath.
    const headOp = clamp(1 - rl / 0.3)
    stage.style.setProperty("--sharp", `${1 - t}`)
    stage.style.setProperty("--reveal", `${rl}`)
    stage.style.setProperty("--head", `${headOp}`)
    stage.style.setProperty("--op", `${op}`)
    stage.style.setProperty("--rise-left", `${c.riseLeft * intervalPx}px`)
    stage.style.setProperty("--rise-panel", `${c.risePanel * intervalPx}px`)
  }, [])

  const applyFromScroll = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const total = track.offsetHeight - window.innerHeight
    totalRef.current = total
    const p =
      total > 0
        ? Math.min(1, Math.max(0, -track.getBoundingClientRect().top / total))
        : 0
    applyProgress(p)
  }, [applyProgress])

  // Scroll-driven blur/reveal (always; autoplay only cycles the sections).
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduce) {
      applyProgress(1)
      return
    }
    let raf = 0
    const onScroll = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0
          applyFromScroll()
        })
    }
    applyFromScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [applyFromScroll, applyProgress])

  // Start autoplay once the stage scrolls into view (pattern from the hormones LP).
  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true)
          io.disconnect()
        }
      },
      { threshold: 0.4 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  // Track reduced-motion so the auto-advance (driven by the progress bar's
  // animationend below) is disabled cleanly rather than firing instantly. The
  // advance itself is not a timer here — it's the bar completing — so the fill
  // and the switch can never drift out of sync, and hover pauses both at once.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const sync = () => setReduced(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  return (
    <>
      <div
        ref={trackRef}
        className="relative w-screen"
        style={{
          height: `${cfg.trackVh}vh`,
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
        }}
      >
        <div
          ref={stageRef}
          className="sticky top-0 h-screen w-full overflow-hidden"
        >
          {/* background — static pre-blurred base + sharp layer that cross-fades
            out. Only opacity/transform animate per frame; the blur is rasterised
            once (no per-frame filter), so scrolling stays at 60fps. */}
          {/* TODO: metabolism imagery — reuses the immunity wellness photo. */}
          <div
            className="absolute inset-0"
            style={{
              // Static over-scale so the blurred layer's soft edges never
              // reveal the container edge (must exceed the blur radius). No
              // scroll-driven zoom.
              transform: "scale(1.12)",
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                filter: `blur(${cfg.maxBlur}px) brightness(${1 - cfg.darken})`,
              }}
            >
              {/* Same unoptimized source as the sharp layer below, so the two
                  are pixel-identical and the cross-fade reads as a pure focus
                  pull with zero sideways drift — an optimized vs raw pair
                  frames slightly differently and looks like a shift. */}
              <Image
                src={blobMediaUrl(
                  "/images/metabolism/immersive-metabolism-stairs.webp"
                )}
                alt=""
                fill
                unoptimized
                sizes="100vw"
                className="object-cover object-center"
              />
            </div>
            <div
              className="absolute inset-0"
              style={{ opacity: "var(--sharp, 1)", willChange: "opacity" }}
            >
              {/* No `priority` — same reasoning as the immunity twin: this is
                  ~2 viewports below the fold and 162 KB `unoptimized`, i.e.
                  more than twice the hero it was competing with. */}
              <Image
                src={blobMediaUrl(
                  "/images/metabolism/immersive-metabolism-stairs.webp"
                )}
                alt=""
                fill
                unoptimized
                sizes="100vw"
                className="object-cover object-center"
              />
            </div>
          </div>
          {/* base legibility gradient (always) + scroll-ramped scrim */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.44) 42%, rgba(0,0,0,0.14) 74%), linear-gradient(0deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 42%)",
            }}
          />
          {/* content — header slides up out of view; the readout settles in the centre */}
          <div className="relative h-full">
            {/* header layer — centred at rest, slides up on scroll */}
            <div className="pointer-events-none absolute inset-0 flex items-center">
              <div className={`${SECTION} w-full`}>
                <div
                  className="flex max-w-[573px] flex-col gap-[30px]"
                  style={{
                    transform: "translateY(calc(var(--reveal, 0) * -60vh))",
                    opacity: "var(--head, 1)",
                  }}
                >
                  <h2 className="type-display-lg text-[#f6f6f6]">
                    {t(k.metabolismImmHeading)}
                  </h2>
                  <p className="type-title-lg max-w-[468px] font-medium text-white">
                    {t(k.metabolismImmSubhead)}
                  </p>
                </div>
              </div>
            </div>

            {/* readout layer — centred; insights + card slide up from below + fade in.
                Mobile adds bottom padding so the centred group sits a little
                higher, leaving dark photo below the white card instead of it
                butting straight into the next (white) section. */}
            <div className="absolute inset-0 flex items-center pt-16 pb-6 md:pt-0 md:pb-0">
              <div className={`${SECTION} w-full`}>
                <div
                  className="flex flex-col gap-5 lg:flex-row lg:items-center lg:gap-16 xl:gap-[130px]"
                  onMouseEnter={() => setHovered(true)}
                  onMouseLeave={() => setHovered(false)}
                >
                  {/* left — insight list (node 143:1361, w 505, gap 40) */}
                  <div
                    className="flex flex-col gap-3 lg:min-w-0 lg:flex-[505_1_0%] lg:gap-10"
                    style={{
                      opacity: "var(--op, 0)",
                      transform:
                        "translateY(calc((1 - var(--reveal, 0)) * var(--rise-left, 72px)))",
                    }}
                  >
                    {INSIGHTS_V2.map((ins, i) => {
                      const isActive = i === activeIndex
                      return (
                        <button
                          key={ins.titleKey}
                          type="button"
                          onClick={() => onPick(i)}
                          aria-pressed={isActive}
                          className="group relative cursor-pointer pl-[19px] text-left"
                        >
                          <span
                            aria-hidden
                            className={`absolute top-0 left-0 h-full w-[3px] rounded-full transition-[background-color,opacity] duration-300 ${
                              isActive
                                ? "bg-[#c14b33] opacity-100"
                                : "bg-[#fdf7f2] opacity-30 group-hover:opacity-70"
                            }`}
                          />
                          {/* auto-advance progress fill — its completion IS the
                              trigger to advance (onAnimationEnd), so bar and
                              switch stay in sync; pauses with the pointer via
                              animationPlayState. Skipped under reduced motion. */}
                          {isActive && started && autoplay && !reduced && (
                            <span
                              key={activeIndex}
                              aria-hidden
                              onAnimationEnd={() =>
                                onPick((activeIndex + 1) % INSIGHTS_V2.length)
                              }
                              className="bg-terracotta-400 absolute top-0 left-0 w-[3px] rounded-full"
                              style={{
                                animation: `immFill ${autoDur * 1000}ms linear forwards`,
                                animationPlayState: hovered
                                  ? "paused"
                                  : "running",
                              }}
                            />
                          )}
                          <h3
                            className={`md:type-headline-sm text-[16px] leading-snug font-medium transition-colors duration-300 ${
                              isActive
                                ? "text-white"
                                : "text-white/45 group-hover:text-white/90"
                            }`}
                          >
                            {t(ins.titleKey)}
                          </h3>
                        </button>
                      )
                    })}
                    {/* CTA under the tabs — only in the lg row layout. On
                        narrower (tablet/mobile) widths it drops to the bottom
                        of the section instead (see the stacked CTA below), so
                        the tabs and readout card sit adjacent. */}
                    <div className="hidden pt-2 lg:block">
                      <BookingLink
                        href={bookingHref}
                        location="metabolism-fullpicture"
                      >
                        <Button
                          size="lg"
                          variant="primary"
                          className="group"
                          iconRight={
                            <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                          }
                        >
                          <CtaLabel
                            label={t(k.metabolismHeroCta1, {
                              price: bloodPriceArg(prices),
                            })}
                          />
                        </Button>
                      </BookingLink>
                    </div>
                  </div>

                  {/* right — readout card (node 143:947, p 48, rounded 32, gap 37).
                      Fixed min-height so the card doesn't jump between insights
                      (tallest content is story 4 — four readings incl. muscle).
                      Sized to the tallest EN story (~583px) plus headroom for
                      longer locales; keep it ahead of that story if copy grows. */}
                  <div
                    className="relative min-h-[416px] w-full rounded-[32px] bg-white p-4 lg:min-h-[620px] lg:min-w-0 lg:flex-[672_1_0%] lg:p-12"
                    style={{
                      opacity: "var(--op, 0)",
                      transform:
                        "translateY(calc((1 - var(--reveal, 0)) * var(--rise-panel, 44px)))",
                    }}
                  >
                    {/* the insight — the takeaway from reading markers together */}
                    <div className="flex flex-col gap-2">
                      <p className="type-label-md hidden text-[#919191] lg:block">
                        {t(k.metabolismImmReadTogether)}
                      </p>
                      <p className="type-headline-md max-w-[464px] pr-7 text-[#232323] md:pr-0">
                        {t(active.leadKey)}
                      </p>
                      {/* fuller explanation of the cross-marker insight */}
                      <p className="type-body-md lg:type-body-lg mt-2 max-w-[464px] text-neutral-700">
                        {t(active.bodyKey)}
                      </p>
                    </div>

                    {/* the markers that, read together, make the point */}
                    <div className="mt-3 flex flex-col lg:mt-[37px]">
                      <p className="type-label-md hidden text-[#919191] lg:block">
                        {t(k.metabolismImmMarkersBehind)}
                      </p>
                      <div className="mt-2 flex flex-col gap-2.5">
                        {active.readings.map((r, i) => (
                          <div key={i} className="flex flex-col gap-[5px]">
                            <div className="flex items-end justify-between gap-4">
                              <div className="flex items-center gap-2 py-1 lg:gap-[14px] lg:py-[7px]">
                                <span className="lg:type-title-lg text-[15px] font-medium text-[#2d2d2d]">
                                  {t(r.markerKey)}
                                </span>
                                {/* status badge is noise on a narrow screen —
                                    the zone bar + dot already show the reading,
                                    so drop it on mobile to keep rows compact */}
                                <span className="hidden lg:inline-flex">
                                  <StatusBadge
                                    tone={r.tone}
                                    label={t(r.badgeKey)}
                                  />
                                </span>
                              </div>
                              <div className="flex items-baseline gap-0.5">
                                <span className="lg:type-display-md text-xl text-[#101828]">
                                  {r.value}
                                </span>
                                <span className="lg:type-title-md text-sm text-[#919191]">
                                  {r.unit}
                                </span>
                              </div>
                            </div>
                            <ZoneBar zones={r.zones} dot={r.dot} />
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* play / pause autoplay — tucked under the card's bottom-right */}
                    <button
                      type="button"
                      onClick={() => setAutoplay((a) => !a)}
                      aria-label={
                        autoplay
                          ? t(k.metabolismImmPause)
                          : t(k.metabolismImmPlay)
                      }
                      className="absolute top-6 right-6 cursor-pointer text-neutral-400/70 transition-colors hover:text-neutral-600"
                    >
                      {autoplay ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M7 4.5v15l12-7.5z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                {/* Stacked (tablet/mobile): CTA drops to the bottom of the
                    section, centred, so the tabs and readout card sit adjacent
                    instead of being split by the button. Shares the readout's
                    reveal (--op / --reveal) so it slides up and fades in with
                    the tabs and card rather than sitting there statically. */}
                <div
                  className="mt-6 flex justify-center lg:hidden"
                  style={{
                    opacity: "var(--op, 0)",
                    transform:
                      "translateY(calc((1 - var(--reveal, 0)) * var(--rise-panel, 44px)))",
                  }}
                >
                  <BookingLink
                    href={bookingHref}
                    location="metabolism-fullpicture"
                    className="block w-full sm:inline-block sm:w-auto"
                  >
                    <Button
                      size="lg"
                      variant="primary"
                      className="group w-full sm:w-auto"
                      iconRight={
                        <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                      }
                    >
                      <CtaLabel
                        label={t(k.metabolismHeroCta1, {
                          price: bloodPriceArg(prices),
                        })}
                      />
                    </Button>
                  </BookingLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes immFill { from { height: 0 } to { height: 100% } }`}</style>
    </>
  )
}

/* ── Placeholder scan-tile motifs for the MRI finding cards (visceral fat,
   liver fat, metabolic organs). Stylised, not real scans — swap for a real next/image MRI
   crop when assets land. Index-aligned to the imaging markers. ── */
const MRI_GLYPHS = [
  // visceral fat — body slice with the visceral depot highlighted
  <svg key="v" viewBox="0 0 40 40" className="h-16 w-16" aria-hidden="true">
    <rect
      x="9"
      y="4"
      width="22"
      height="32"
      rx="11"
      className="fill-none stroke-white/40"
      strokeWidth="2"
    />
    <circle cx="20" cy="21" r="6" className="fill-white/15" />
    <path
      d="M20 11 a10 10 0 0 1 0 20"
      className="stroke-bright-green-300 fill-none"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
  </svg>,
  // liver fat — liver blob with a fat "level" fill
  <svg key="l" viewBox="0 0 40 40" className="h-16 w-16" aria-hidden="true">
    <path
      d="M7 15 C7 9 33 9 33 16 C33 26 24 30 20 30 C13 30 7 25 7 15 Z"
      className="fill-none stroke-white/40"
      strokeWidth="2"
    />
    <path
      d="M8.5 22 C14 26 26 26 31.5 22 C30.5 27 25 30 20 30 C14 30 9.5 27 8.5 22 Z"
      className="fill-bright-green-300/70"
    />
  </svg>,
  // muscle & body composition — flexed arm with the bicep highlighted
  <svg key="m" viewBox="0 0 40 40" className="h-16 w-16" aria-hidden="true">
    <path
      d="M7 31 h7 a6 6 0 0 0 6 -6 v-2 a6 6 0 0 1 6 -6 h4"
      className="fill-none stroke-white/40"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* bicep bulge */}
    <path
      d="M8 30 C7 23 12 20 16 23 C14 28 11 30 8 30 Z"
      className="fill-bright-green-300/70"
    />
  </svg>,
]

/* ── Per-marker card captions (AHD-10317). Index-aligned to the imaging
   markers (visceral fat, liver fat, muscle).

   Each caption is a STANDALONE sentence that names its own subject. It used to
   be a hardcoded English fragment appended to the translated marker name
   (`<span>Visceral fat</span> is the hidden fat …`), which only parses in
   English: French needs the article ("La graisse viscérale est …", and the
   chip label cannot carry it), and cards 1-2 take a singular verb while card 3
   is a coordinate plural ("Muskeln & Körperzusammensetzung", "Spieren &
   samenstelling"). No single continuation template survives all six languages,
   so the sentence carries its own subject and the marker name is left to the
   reading chip that sits directly above it. ── */
const MRI_NOTE_KEYS = [
  k.metabolismMriCardNote1,
  k.metabolismMriCardNote2,
  k.metabolismMriCardNote3,
] as const

/* ── Real MRI card backgrounds (index-aligned to the imaging markers). Where a
   card has an image it replaces the placeholder scan-tile + glyph; empty string
   falls back to the stylised glyph tile until a real crop lands. ── */
const MRI_IMAGES = [
  blobMediaUrl("/images/metabolism/card-visceral-mri.webp"),
  blobMediaUrl("/images/metabolism/card-liver-fat-v3.webp"),
  // Coronal whole-body MRI with the AMRA muscle segmentation overlaid on the
  // lumbar muscles — same body-composition source + framing as the visceral
  // card, so the three cards read as one scan.
  blobMediaUrl("/images/metabolism/card-muscle-mri.webp"),
]

/* Per-marker reading (index-aligned). Visceral + liver are measured values →
   vertical range bar ("you" near the top/terracotta for elevated/high). The
   metabolic-organs card is a structural finding → a severity-dot gauge instead
   (benign observation = 1 green dot). */
const MRI_CHARTS: (ReadingChart | undefined)[] = [
  { youTopPct: 74, youTone: "green", cohortTopPct: 40, cohortLeftPct: 34 },
  { youTopPct: 48, youTone: "sand", cohortTopPct: 62, cohortLeftPct: 34 },
  // muscle: the bar is a health axis (green = bottom), not a value axis, so
  // healthy muscle sits low in the green band like visceral — not up top
  { youTopPct: 74, youTone: "green", cohortTopPct: 48, cohortLeftPct: 34 },
]
const MRI_FINDINGS: (Finding[] | undefined)[] = [
  undefined,
  undefined,
  undefined,
]

/* ── MRI beat, immersive variant — a pinned scroll section. One column
   translates as a whole: at rest it's pushed down so the heading sits centred,
   and on scroll it rides up so the heading docks near the top (and stays) while
   the three white photo-cards come up into view beneath it and fade in.
   Typography matches the full-picture immersive. Each card leads with a
   vertical photo placeholder (dark scan tile → real MRI crop later) over the
   reading + a short caption. Below md it degrades to a plain static stack. ── */
export function MetabolismMriImmersive() {
  const t = useTranslations("go")
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)

  // The imaging markers an MRI reads that blood can't. Visceral + liver fat come
  // from the shared immersive readout (Story 4), so their levels stay in one
  // place; the third card is a dedicated MUSCLE / body-composition finding —
  // decoupled from Story 4's "metabolic organs" so the beat leads with muscle,
  // the body's largest metabolic tissue, without changing the §5 story.
  const imagingReadout =
    INSIGHTS_V2.find((s) =>
      s.readings.some((r) => r.groupKey === k.metabolismGroupImaging)
    )?.readings ?? []
  const mriMarkers = [
    ...imagingReadout.slice(0, 2),
    {
      markerKey: k.metabolismMkMuscle,
      value: "",
      unit: "",
      badgeKey: k.metabolismBadgeNormal,
    },
  ]

  // SSR + first client render pin (desktop-first); a phone flips to the static
  // stack after mount — same-value initial render, so no hydration mismatch.
  const [pinned, setPinned] = useState(true)
  useEffect(() => {
    // Pinned scroll-reveal on tablet + desktop (md+). Only mobile is
    // non-interactive, so it gets the plain static stack instead.
    const mq = window.matchMedia("(min-width: 768px)")
    const sync = () => setPinned(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  // Reveal window (fraction of the pinned track the reveal plays over).
  const REVEAL_START = 0.08
  const REVEAL_END = 0.95

  const applyProgress = useCallback((p: number) => {
    const stage = stageRef.current
    if (!stage) return
    const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v))
    const smooth = (x: number) => x * x * (3 - 2 * x)
    // Reveal progress across the pinned track.
    const rl = clamp((p - REVEAL_START) / (REVEAL_END - REVEAL_START))
    // Blur cross-fade: the photo is sharp at rest, then blurs EARLY (before the
    // cards). --sharp 1→0 fades the sharp layer out over the blurred one.
    const blurP = smooth(clamp((p - 0.08) / 0.22))
    // Cards fade in once the header is on its way out.
    const op = smooth(clamp((rl - 0.3) / 0.62))
    // The header leaves in two beats rather than stopping all at once: the
    // eyebrow + body line go first (--sub), then the heading (--head). --head
    // also means the heading is invisible well before the travel ends, so it is
    // never guillotined at full opacity by the stage's overflow-hidden edge.
    const sub = 1 - smooth(clamp(rl / 0.28))
    const head = 1 - smooth(clamp(rl / 0.42))
    stage.style.setProperty("--reveal", `${rl}`)
    stage.style.setProperty("--sharp", `${1 - blurP}`)
    stage.style.setProperty("--op", `${op}`)
    stage.style.setProperty("--sub", `${sub}`)
    stage.style.setProperty("--head", `${head}`)
    // The cards layer covers the whole stage, so it must not swallow clicks
    // while it is still transparent.
    stage.style.setProperty("--cards-pe", op > 0.5 ? "auto" : "none")
  }, [])

  const applyFromScroll = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const total = track.offsetHeight - window.innerHeight
    const p =
      total > 0
        ? Math.min(1, Math.max(0, -track.getBoundingClientRect().top / total))
        : 0
    applyProgress(p)
  }, [applyProgress])

  // Scroll-driven reveal — only while pinned (md+). On mobile the static stack
  // shows everything, so no listener runs.
  useEffect(() => {
    if (!pinned) return
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduce) {
      applyProgress(1)
      return
    }
    let raf = 0
    const onScroll = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0
          applyFromScroll()
        })
    }
    applyFromScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [pinned, applyFromScroll, applyProgress])

  const textBlock = (
    <>
      <p className="type-label-md text-white/50 uppercase">
        {t(k.metabolismMriEyebrow)}
      </p>
      <h2 className="type-display-lg text-neutral-50">
        {t(k.metabolismMriHeading)}
      </h2>
      <p className="type-title-lg max-w-[560px] font-medium text-white">
        {t(k.metabolismMriBody)}
      </p>
    </>
  )

  const cardsBlock = (
    // md+ uses two shared rows via subgrid — tiles, then captions — so every
    // tile gives back the same height and their bottoms stay aligned even
    // though the captions wrap to different depths. The row gap is dropped
    // there because the caption carries its own mt-4. On the static mobile
    // branch each card is a plain flex column at the natural 4:5 ratio.
    <div className="mx-auto grid min-h-0 w-full max-w-[960px] shrink grid-cols-1 gap-5 md:grid-cols-3 md:grid-rows-[minmax(0,1fr)_auto] md:gap-y-0">
      {mriMarkers.map((r, i) => (
        // Image card with the reading chip docked at the bottom; the title +
        // description sit OUTSIDE the card, under the image.
        <div
          key={i}
          className="flex min-h-0 flex-col md:row-span-2 md:grid md:grid-rows-subgrid"
        >
          {/* 4:5 is the tile's natural size and its ceiling: it sets the row's
              max-content height, and the grid above only shrinks, so a tall
              viewport stops here. md:h-full makes the height definite so the
              tile actually follows the row when it shrinks — aspect-ratio
              alone outranks stretch and would overflow instead. Short
              viewports therefore crop the photo (object-cover) rather than
              push the caption and the CTA row past the bottom of the stage. */}
          <div className="relative aspect-[4/5] min-h-0 w-full overflow-hidden rounded-[24px] bg-neutral-100 md:h-full">
            {MRI_IMAGES[i] ? (
              // real MRI crop — full-bleed
              <Image
                src={MRI_IMAGES[i]}
                alt=""
                aria-hidden
                fill
                sizes="(min-width: 768px) 320px, 100vw"
                className="object-cover"
              />
            ) : (
              // stylised placeholder scan-tile + glyph until a real crop lands
              <>
                <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
                <div
                  aria-hidden
                  className="bg-bright-green-300/10 absolute top-[38%] left-1/2 h-28 w-28 -translate-x-1/2 rounded-full blur-2xl"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  {MRI_GLYPHS[i]}
                </div>
              </>
            )}
            {/* light gradient to seat the reading chip */}
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent" />
            {/* reading chip (title + reading + range chart), single row */}
            <div className="absolute inset-x-3 bottom-3 z-10 md:inset-x-4 md:bottom-4">
              <MetabolismReadingChip
                title={t(r.markerKey)}
                value={r.value || undefined}
                unit={r.unit || undefined}
                statusLabel={t(r.badgeKey)}
                chart={MRI_CHARTS[i]}
                findings={MRI_FINDINGS[i]}
              />
            </div>
          </div>
          {/* description — one standalone sentence per card. The marker name
              is NOT repeated here: the reading chip docked at the bottom of the
              tile above already carries it in white. min-height keeps the three
              cards the same total height; `shrink-0` keeps it readable in full
              when the tile above it is giving height back. */}
          <p className="mt-4 min-h-[3.5rem] shrink-0 text-lg leading-snug font-medium text-white/70">
            {t(MRI_NOTE_KEYS[i])}
          </p>
        </div>
      ))}
    </div>
  )

  // Soft secondary link — the MRI is an upsell, not the primary offer, so this
  // just points to the pricing block rather than opening booking.
  const ctaBlock = (
    // Canonical DS lg CTA sizing (h-52 · px-10 · type-body-lg), white outline
    // for the dark section — matches the primary buttons elsewhere.
    <a
      href="#pricing"
      className="type-body-lg group inline-flex h-[52px] items-center justify-center gap-2 rounded-full border border-white/40 px-10 font-medium text-white transition-colors hover:bg-white/10"
    >
      {t(k.metabolismMriSectionCta)}
      <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
    </a>
  )

  // Full-bleed MRI-suite photo (Siemens Healthineers) behind the section.
  // While pinned it fades IN with scroll progress (--reveal) rather than
  // showing immediately; a light dark scrim keeps the white heading + cards
  // legible. On mobile (static) it's simply shown.
  const bgSrc = blobMediaUrl("/images/metabolism/mri-suite-siemens.webp")
  const bg = (
    <div aria-hidden className="absolute inset-0">
      {/* sharp layer — visible + sharp at rest; fades out as the blur takes
          over on scroll (--sharp 1→0). Only opacity animates (no per-frame
          filter), so scrolling stays smooth. */}
      <div
        className="absolute inset-0"
        style={{ opacity: pinned ? "var(--sharp, 1)" : "0" }}
      >
        <Image
          src={bgSrc}
          alt=""
          fill
          sizes="100vw"
          className="scale-110 object-cover"
        />
      </div>
      {/* blurred layer — same image, fades in as you scroll */}
      <div
        className="absolute inset-0"
        style={{ opacity: pinned ? "calc(1 - var(--sharp, 1))" : "1" }}
      >
        <Image
          src={bgSrc}
          alt=""
          fill
          sizes="100vw"
          className="scale-110 object-cover blur-xl"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/55 via-neutral-950/20 to-neutral-950/50" />
    </div>
  )

  // Mobile: no pin, no reveal — a plain centred stack, everything visible.
  if (!pinned) {
    return (
      <section className="relative overflow-hidden bg-neutral-900 py-20 text-white">
        {bg}
        <div className={`${SECTION} relative z-10 w-full`}>
          <div className="mx-auto flex max-w-[760px] flex-col items-center gap-5 text-center">
            {textBlock}
          </div>
          <div className="mt-10">{cardsBlock}</div>
          <div className="mt-10 flex justify-center">{ctaBlock}</div>
        </div>
      </section>
    )
  }

  // Desktop/tablet: pinned track with two overlapping centred layers — the
  // header rides up and out, the cards rise in to replace it (like the
  // full-picture immersive).
  return (
    <div
      ref={trackRef}
      className="relative w-screen"
      style={{
        height: "240vh",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
      }}
    >
      <div
        ref={stageRef}
        className="sticky top-0 h-screen w-full overflow-hidden bg-neutral-900 text-white"
      >
        {bg}
        {/* Two overlapping layers, both bounded to the stage (inset-0) so
            neither can spill past 100vh — the same shape the full-picture
            immersive at the top of this file uses. The header rides up and out
            as --sub falls, which is what frees the height for the cards; the
            cards then have the whole stage to lay out in. */}
        <div className="relative h-full">
          {/* Header layer — centred at rest, slides up and out on scroll. The
              eyebrow + body line fade on the way (opacity only, as before). */}
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-center"
            style={{
              opacity: "var(--head, 1)",
              transform: "translateY(calc(var(--reveal, 0) * -60vh))",
            }}
          >
            <div className={`${SECTION} w-full`}>
              <div className="mx-auto flex max-w-[760px] flex-col items-center gap-5 text-center">
                <p
                  className="type-label-md text-white/50 uppercase"
                  style={{ opacity: "var(--sub, 1)" }}
                >
                  {t(k.metabolismMriEyebrow)}
                </p>
                <h2 className="type-display-lg text-neutral-50">
                  {t(k.metabolismMriHeading)}
                </h2>
                <p
                  className="type-title-lg max-w-[560px] font-medium text-white"
                  style={{ opacity: "var(--sub, 1)" }}
                >
                  {t(k.metabolismMriBody)}
                </p>
              </div>
            </div>
          </div>
          {/* Cards layer — fades in as the header leaves and gets the full
              stage height. Top padding clears the fixed navbar pill (36px
              offset + 72px tall). The grid inside only shrinks, never grows, so
              on a tall viewport the tiles stop at their natural 4:5 and
              justify-center recentres the block instead of stretching it. */}
          <div
            className={`${SECTION} [pointer-events:var(--cards-pe,none)] absolute inset-0 z-10 flex flex-col justify-center pt-[7.25rem] pb-8`}
            style={{ opacity: "var(--op, 0)" }}
          >
            {cardsBlock}
            <div className="mt-6 flex shrink-0 justify-center">{ctaBlock}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
